/**
 * Snapchat Advertising OAuth Utilities (Story 19.7, Task 2)
 *
 * Handles OAuth 2.0 + PKCE URL building, code exchange, refresh, and error
 * parsing against the Snap Marketing API v1 endpoints
 * (`https://accounts.snapchat.com/login/oauth2/authorize` for authorize,
 *  `https://accounts.snapchat.com/login/oauth2/access_token` for token).
 *
 * Key characteristics that distinguish Snap from Pinterest / Microsoft Ads:
 *  - Form-body credentials on the token endpoint (`client_id` + `client_secret`
 *    in the form body — NOT HTTP Basic Auth like Pinterest. Matches Microsoft
 *    Ads / Klaviyo).
 *  - Aggressive 60-minute access token TTL — refresh required well before
 *    expiry. Refresh tokens are treated as effectively permanent (Snap docs
 *    do not specify a TTL; observed behaviour matches Klaviyo). The adapter
 *    defensively persists any rotated refresh_token returned on each refresh.
 *  - PKCE (code_challenge_method=S256) sent for defense-in-depth and uniformity
 *    with Klaviyo + Microsoft Ads + Pinterest. Snap tolerates PKCE on
 *    confidential clients without rejecting.
 *  - `redirect_uri` is mandatory on every `/access_token` call since 2019-10-01;
 *    even trailing-slash differences fail with `invalid_grant`.
 *  - Scopes: `snapchat-marketing-api` (read-only Ads API access — single scope
 *    in 19-7; offline-conversions and profile scopes are out of scope and
 *    require separate Snap review).
 *  - Scope validation: if Snap silently narrows scope (returns `""` or omits
 *    `snapchat-marketing-api`), the adapter fails loud with a clear error and
 *    tokens are NOT persisted (silent scope-narrowing is a known OAuth attack
 *    vector — fail loud, never store under-scoped tokens).
 *
 * @see https://developers.snap.com/api/marketing-api/Ads-API/authentication
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const SNAPCHAT_AUTHORIZE_URL =
  "https://accounts.snapchat.com/login/oauth2/authorize";
const SNAPCHAT_TOKEN_URL =
  "https://accounts.snapchat.com/login/oauth2/access_token";
const TOKEN_EXCHANGE_TIMEOUT_MS = 25_000;

/**
 * Scopes requested for Snapchat Advertising integration.
 *
 * `snapchat-marketing-api` grants read-only access to the Snap Marketing API
 * (campaigns, ads, stats). `snapchat-offline-conversions-api` and
 * `snapchat-profile-api` are NOT requested in 19-7 — they require separate
 * Snap review and are out of scope.
 */
export const SNAPCHAT_ADS_SCOPES = ["snapchat-marketing-api"] as const;

// ============================================================================
// Types
// ============================================================================

export interface SnapchatAdsAuthUrlOptions {
  clientId: string;
  redirectUri: string;
  /** KV state key to embed in state param */
  stateKey: string;
  /** PKCE code_challenge (Base64URL of SHA-256 of code_verifier) */
  codeChallenge: string;
}

export interface SnapchatAdsTokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  scope: string;
}

export interface SnapchatAdsCallbackError {
  code: string;
  userMessage: string;
  userAction: "retry" | "none";
}

// ============================================================================
// Build Authorization URL
// ============================================================================

/**
 * Builds the Snapchat Marketing API OAuth 2.0 + PKCE authorization URL.
 *
 *   - Authorize URL is `https://accounts.snapchat.com/login/oauth2/authorize`
 *   - `scope` is space-joined per OAuth 2.0 spec
 *   - `code_challenge_method=S256` for defense-in-depth (Snap tolerates PKCE
 *     on confidential clients)
 */
export function buildSnapchatAdsAuthUrl(
  options: SnapchatAdsAuthUrlOptions
): string {
  const { clientId, redirectUri, stateKey, codeChallenge } = options;

  if (!clientId) {
    throw new Error("SNAPCHAT_ADS_CLIENT_ID is required");
  }
  if (!redirectUri) {
    throw new Error("SNAPCHAT_ADS_OAUTH_REDIRECT_URI is required");
  }
  if (!stateKey) {
    throw new Error("stateKey is required for CSRF protection");
  }
  if (!codeChallenge) {
    throw new Error("codeChallenge is required for PKCE");
  }

  const url = new URL(SNAPCHAT_AUTHORIZE_URL);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", clientId);
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("state", stateKey);
  url.searchParams.set("scope", SNAPCHAT_ADS_SCOPES.join(" "));
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", codeChallenge);

  return url.toString();
}

// ============================================================================
// Typed token-endpoint error (so adapter callers don't regex-match messages)
// ============================================================================

/**
 * Thrown by `exchangeSnapchatAdsCode` / `refreshSnapchatAdsToken` when Snap's
 * token endpoint returns a non-2xx response. Carries the parsed OAuth `error`
 * code field (when present) so the adapter can route `invalid_grant` →
 * `token_expired` without string-matching the (sanitized) message.
 */
export interface SnapchatAdsTokenErrorOptions {
  /**
   * The verbatim value of the `error` field from Snap's JSON envelope
   * (e.g., `"invalid_grant"`, `"invalid_request"`); undefined when the
   * response body wasn't a parseable OAuth-shaped JSON envelope.
   */
  oauthError?: string;
}

export class SnapchatAdsTokenError extends Error {
  readonly oauthError: string | undefined;
  constructor(message: string, options: SnapchatAdsTokenErrorOptions = {}) {
    super(message);
    this.name = "SnapchatAdsTokenError";
    this.oauthError = options.oauthError;
  }
}

/**
 * Pulls the OAuth `error` code out of a sanitized provider body. Returns
 * undefined when the body isn't JSON, doesn't carry an `error` field, or
 * carries it in an unexpected shape.
 *
 * @internal exported for direct unit testing only
 */
export function extractOAuthErrorCode(
  providerBody: string | undefined
): string | undefined {
  if (!providerBody) return undefined;
  try {
    const parsed: unknown = JSON.parse(providerBody);
    if (parsed === null || typeof parsed !== "object") return undefined;
    const err = (parsed as Record<string, unknown>).error;
    if (typeof err === "string" && err.length > 0) {
      return err;
    }
    if (err !== null && typeof err === "object") {
      const nestedCode = (err as Record<string, unknown>).code;
      if (typeof nestedCode === "string" && nestedCode.length > 0) {
        return nestedCode;
      }
      const nestedError = (err as Record<string, unknown>).error;
      if (typeof nestedError === "string" && nestedError.length > 0) {
        return nestedError;
      }
    }
  } catch {
    // Not JSON — return undefined so callers fall back to the message body.
  }
  return undefined;
}

/**
 * Pulls the most descriptive message we can find out of a Snap token error
 * envelope, with empty-string fallback when nothing structured is present.
 *
 * Snap's token-endpoint errors come back as the standard OAuth 2.0 form:
 *   `{ "error": "invalid_grant", "error_description": "..." }`
 */
export function extractOAuthErrorDescription(providerBody: string): string {
  try {
    const parsed = JSON.parse(providerBody) as {
      error?: unknown;
      error_description?: unknown;
      message?: unknown;
    };
    const err =
      typeof parsed.error === "string" && parsed.error.length > 0
        ? parsed.error
        : "";
    const desc =
      typeof parsed.error_description === "string" &&
      parsed.error_description.length > 0
        ? parsed.error_description
        : "";
    const msg =
      typeof parsed.message === "string" && parsed.message.length > 0
        ? parsed.message
        : "";

    if (err && desc) return `${err}: ${desc}`;
    if (desc) return desc;
    if (err) return err;
    if (msg) return msg;
    return "";
  } catch {
    // Not JSON — empty string fallback.
    return "";
  }
}

// ============================================================================
// Token Exchange
// ============================================================================

/**
 * Exchanges an authorization code for access + refresh tokens.
 *
 * CRITICAL: Snap requires `application/x-www-form-urlencoded` for the token
 * endpoint AND form-body credentials (NOT HTTP Basic Auth like Pinterest).
 * `code_verifier` is sent so Snap can validate the PKCE pair.
 *
 * The returned token response is validated:
 *   1. `access_token` + `refresh_token` are non-empty strings
 *   2. `expires_in` is a finite number (NaN-prevention for arithmetic)
 *   3. `scope` includes `snapchat-marketing-api` — silent scope-narrowing
 *      is rejected loudly with a `scope-narrowed` error (AC #2 invariant).
 */
export async function exchangeSnapchatAdsCode(
  code: string,
  codeVerifier: string,
  clientId: string,
  clientSecret: string,
  redirectUri: string
): Promise<SnapchatAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    client_secret: clientSecret,
    code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  });

  const result = await safeFetch(
    SNAPCHAT_TOKEN_URL,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new SnapchatAdsTokenError(
      sanitizeErrorMessage(
        `Snapchat Ads token exchange failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as SnapchatAdsTokenResponse;

  validateTokenResponse(data, /* requireRefreshToken */ true);
  validateScope(data.scope);

  return data;
}

// ============================================================================
// Token Refresh
// ============================================================================

/**
 * Refreshes a Snapchat Ads access token via the refresh_token grant.
 *
 * Snap's documentation does NOT explicitly state whether refresh tokens rotate
 * on every refresh. The response *always* includes a `refresh_token` field,
 * so the adapter MUST persist it whenever non-empty (defensive — guards
 * against silent rotation policy changes). If the response omits refresh_token
 * or returns an empty string, the caller falls back to the existing one.
 */
export async function refreshSnapchatAdsToken(
  refreshToken: string,
  clientId: string,
  clientSecret: string
): Promise<SnapchatAdsTokenResponse> {
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    client_secret: clientSecret,
    refresh_token: refreshToken,
  });

  const result = await safeFetch(
    SNAPCHAT_TOKEN_URL,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    },
    TOKEN_EXCHANGE_TIMEOUT_MS
  );

  if (!result.success) {
    const detail = result.error.providerMessage
      ? extractOAuthErrorDescription(result.error.providerMessage)
      : "";
    const oauthError = extractOAuthErrorCode(result.error.providerMessage);
    throw new SnapchatAdsTokenError(
      sanitizeErrorMessage(
        `Snapchat Ads token refresh failed: ${detail || result.error.message}`
      ),
      { oauthError }
    );
  }

  const response = result.data;
  const data = (await response.json()) as SnapchatAdsTokenResponse;

  // Refresh responses MAY omit refresh_token (when Snap chooses not to rotate
  // — though observed behaviour shows the field is always populated). The
  // caller (snapchatAdsAdapter.refreshToken) handles the missing value by
  // reusing the existing refresh_token.
  validateTokenResponse(data, /* requireRefreshToken */ false);

  // 31.14 code review: scope-narrowing must fail loud on REFRESH too, not
  // just the initial exchange — discovery emits `scopes` as truthful catalog
  // metadata, and most discovery runs (31-24 cron) execute on refreshed
  // tokens. Per RFC 6749 §6 the refresh response's `scope` is OPTIONAL when
  // unchanged, so an absent/empty scope is tolerated (means "same as
  // granted"); a PRESENT scope that omits `snapchat-marketing-api` is a
  // narrowed token and is rejected before it can be persisted.
  if (typeof data.scope === "string" && data.scope.length > 0) {
    validateScope(data.scope);
  }

  return data;
}

// ============================================================================
// Internal: token-response validation
// ============================================================================

/**
 * Validates that a Snap token response has the fields we require and that
 * they're the correct type. Snap documents `expires_in` as an integer but a
 * future schema drift (e.g., `"3600"` string) would silently break
 * `Date.now() + expires_in * 1000` — string-multiplication yields NaN.
 * We catch that here and throw a clear error instead.
 */
export function validateTokenResponse(
  data: SnapchatAdsTokenResponse,
  requireRefreshToken: boolean
): void {
  if (typeof data.access_token !== "string" || data.access_token.length === 0) {
    throw new Error(
      "Invalid Snapchat Ads token response: missing access_token"
    );
  }
  if (requireRefreshToken) {
    if (
      typeof data.refresh_token !== "string" ||
      data.refresh_token.length === 0
    ) {
      throw new Error(
        "Invalid Snapchat Ads token response: missing refresh_token"
      );
    }
  }
  if (typeof data.expires_in !== "number" || !Number.isFinite(data.expires_in)) {
    throw new Error(
      `Invalid Snapchat Ads token response: expires_in must be a finite number (received ${typeof data.expires_in})`
    );
  }
}

/**
 * Validates that the returned scope contains the requested
 * `snapchat-marketing-api` scope. Silent scope-narrowing is a known OAuth
 * attack vector; if Snap returns `""` or omits our scope, fail loud and never
 * store under-scoped tokens (AC #2 invariant).
 *
 * Snap returns scope as a space-separated string (RFC 6749 §3.3).
 *
 * Error messages embed the provider-returned scope value. We pass it through
 * `sanitizeErrorMessage` defensively — if a future Snap response ever echoed
 * a token-shaped substring inside the `scope` field (e.g. malformed proxy
 * response), we'd never want that surfacing in error logs.
 *
 * @internal exported for direct unit testing only
 */
export function validateScope(scope: string | undefined): void {
  if (typeof scope !== "string" || scope.length === 0) {
    throw new SnapchatAdsTokenError(
      "Snapchat Ads scope-narrowed: provider returned empty scope",
      { oauthError: "scope-narrowed" }
    );
  }
  const granted = scope.split(/\s+/).filter(Boolean);
  for (const required of SNAPCHAT_ADS_SCOPES) {
    if (!granted.includes(required)) {
      throw new SnapchatAdsTokenError(
        sanitizeErrorMessage(
          `Snapchat Ads scope-narrowed: required scope "${required}" not granted (received "${scope}")`
        ),
        { oauthError: "scope-narrowed" }
      );
    }
  }
}

// ============================================================================
// Callback Error Parsing
// ============================================================================

/**
 * Parses Snap Marketing API OAuth callback errors from query params.
 *
 * Snap returns standard OAuth 2.0 error codes as
 * `?error=...&error_description=...`. We map them to user-facing messages
 * and a retry hint.
 */
export function parseSnapchatAdsCallbackError(
  searchParams: URLSearchParams
): SnapchatAdsCallbackError | null {
  const error = searchParams.get("error");
  if (!error) return null;

  if (error === "access_denied") {
    return {
      code: "oauth-denied",
      userMessage: "You denied the Snapchat Advertising connection request.",
      userAction: "retry",
    };
  }

  if (
    error === "invalid_request" ||
    error === "unauthorized_client" ||
    error === "redirect_uri_mismatch"
  ) {
    return {
      code: "oauth-config-error",
      userMessage:
        "Snapchat Advertising connection is misconfigured. Contact support.",
      userAction: "none",
    };
  }

  if (error === "invalid_scope") {
    return {
      code: "oauth-config-error",
      userMessage:
        "Snapchat Advertising connection is misconfigured (invalid scopes). Contact support.",
      userAction: "none",
    };
  }

  if (error === "server_error" || error === "temporarily_unavailable") {
    return {
      code: "oauth-error",
      userMessage:
        "Snapchat Advertising is temporarily unavailable. Please try again later.",
      userAction: "retry",
    };
  }

  if (error === "invalid_grant") {
    // In the callback path, invalid_grant means the auth code expired or was
    // replayed. User-actionable: try connecting again.
    return {
      code: "oauth-error",
      userMessage:
        "Your Snapchat sign-in session expired. Please try connecting again.",
      userAction: "retry",
    };
  }

  return {
    code: "oauth-error",
    userMessage:
      "An error occurred connecting to Snapchat Advertising. Please try again.",
    userAction: "retry",
  };
}
